import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Search, Plus, Building2, RefreshCw, Clock, X } from 'lucide-react';
import { listProduttori } from '../api/produttori';
import type { Produttore, ProduttoreList, StatoOmologhe } from '../types/produttore';
import { useAuthStore } from '../store/authStore';
import { SkeletonRow } from '../components/ui/Skeleton';
import Badge from '../components/ui/Badge';
import i18n from '../i18n';
import { canWriteAnagraficaCommerciale } from '../lib/constants';

// ── Helpers ─────────────────────────────────────────────────────────────────

function formatDateIT(iso: string): string {
  return new Date(iso).toLocaleDateString('it-IT', {
    day: '2-digit', month: 'short', year: 'numeric',
  });
}

function tipoSoggettoBadge(tipo: string | null) {
  if (!tipo) return null;
  const MAP: Record<string, { label: string; variant: 'info' | 'neutral' | 'purple' }> = {
    azienda: { label: i18n.t('anagrafiche.tipo_soggetto.azienda'), variant: 'info' },
    privato: { label: i18n.t('anagrafiche.tipo_soggetto.privato'), variant: 'neutral' },
    ente_pubblico: { label: i18n.t('anagrafiche.tipo_soggetto.ente_pubblico'), variant: 'purple' },
  };
  return MAP[tipo] ?? { label: tipo, variant: 'neutral' as const };
}

function omologheBadge(stato: StatoOmologhe | null | undefined): {
  label: string; variant: 'success' | 'warning' | 'neutral';
} {
  const MAP: Record<StatoOmologhe, { label: string; variant: 'success' | 'warning' | 'neutral' }> = {
    valida: { label: i18n.t('anagrafiche.produttori.omo_valida'), variant: 'success' },
    scaduta: { label: i18n.t('anagrafiche.produttori.omo_scaduta'), variant: 'warning' },
    nessuna: { label: i18n.t('anagrafiche.produttori.omo_nessuna'), variant: 'neutral' },
  };
  return MAP[stato ?? 'nessuna'] ?? MAP.nessuna;
}

// ── Pagination ────────────────────────────────────────────────────────────────

function Pagination({ page, pages, onPage }: { page: number; pages: number; onPage: (p: number) => void }) {
  if (pages <= 1) return null;
  const nums: (number | '...')[] = [];
  if (pages <= 7) {
    for (let i = 1; i <= pages; i++) nums.push(i);
  } else {
    nums.push(1, 2);
    if (page > 4) nums.push('...');
    for (let i = Math.max(3, page - 1); i <= Math.min(pages - 2, page + 1); i++) nums.push(i);
    if (page < pages - 3) nums.push('...');
    nums.push(pages - 1, pages);
  }
  return (
    <div className="flex items-center gap-1 flex-wrap">
      <button
        type="button"
        onClick={() => onPage(page - 1)}
        disabled={page === 1}
        className="px-3 py-1.5 text-sm border border-stone-border rounded-md text-ink-mid hover:bg-stone-bg disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
      >
        ‹
      </button>
      {nums.map((n, i) =>
        n === '...' ? (
          <span key={`ellipsis-${i}`} className="px-2 text-ink-faint text-sm">…</span>
        ) : (
          <button
            key={n}
            type="button"
            onClick={() => onPage(n as number)}
            className={`px-3 py-1.5 text-sm border rounded-md transition-colors ${
              n === page
                ? 'bg-sage text-white border-sage font-semibold'
                : 'border-stone-border text-ink-mid hover:bg-stone-bg'
            }`}
          >
            {n}
          </button>
        )
      )}
      <button
        type="button"
        onClick={() => onPage(page + 1)}
        disabled={page === pages}
        className="px-3 py-1.5 text-sm border border-stone-border rounded-md text-ink-mid hover:bg-stone-bg disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
      >
        ›
      </button>
    </div>
  );
}

// ── Componente principale ─────────────────────────────────────────────────────

const PAGE_LIMIT = 25;

export default function ProduttoriPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuthStore();
  const userRole = user?.role ?? '';
  const canCreate = canWriteAnagraficaCommerciale(userRole);

  const [data, setData] = useState<ProduttoreList | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  // Filtro "omologhe in scadenza ≤30gg", inizializzato dal query param
  // (deep-link dalla coda di lavoro in dashboard) e riflesso nell'URL.
  const soloInScadenza = searchParams.get('omologhe') === 'in_scadenza';
  const setSoloInScadenza = (v: boolean) => {
    setSearchParams((p) => {
      if (v) p.set('omologhe', 'in_scadenza');
      else p.delete('omologhe');
      return p;
    });
    setPage(1);
  };

  // Debounce search 300ms
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  // useCallback + IIFE async interna: nessun setState sincrono nel corpo
  // dell'effect (regola set-state-in-effect) e dipendenza stabile per l'effect.
  const fetchData = useCallback(() => {
    void (async () => {
      setLoading(true);
      setError(null);
      try {
        const result = await listProduttori({
          page,
          limit: PAGE_LIMIT,
          ...(debouncedSearch ? { search: debouncedSearch } : {}),
          ...(soloInScadenza ? { omologhe_in_scadenza: true } : {}),
        });
        setData(result);
      } catch {
        setError(t('anagrafiche.produttori.errore_caricamento'));
      } finally {
        setLoading(false);
      }
    })();
  }, [page, debouncedSearch, soloInScadenza, t]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const items: Produttore[] = data?.items ?? [];

  return (
    <div className="p-8">
      {/* Header */}
      <div className="flex items-start justify-between gap-6 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-ink-dark tracking-tight mb-1">{t('anagrafiche.produttori.titolo')}</h1>
          {data && (
            <span className="text-sm text-ink-soft">
              <strong className="text-ink-dark font-semibold">{data.total}</strong> {t('anagrafiche.produttori.totali_suffix')}
            </span>
          )}
        </div>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={fetchData}
            disabled={loading}
            className="flex items-center gap-2 px-3.5 py-2 bg-white border border-stone-border rounded-lg text-sm text-ink-mid hover:bg-stone-bg transition-colors disabled:opacity-50"
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
            {t('anagrafiche.produttori.aggiorna')}
          </button>
          {canCreate && (
            <button
              type="button"
              onClick={() => navigate('/produttori/nuovo')}
              className="flex items-center gap-2 px-4 py-2 bg-sage text-white rounded-lg text-sm font-medium hover:bg-sage-dark transition-colors"
            >
              <Plus size={15} />
              {t('anagrafiche.produttori.nuovo')}
            </button>
          )}
        </div>
      </div>

      {/* Toolbar */}
      <div className="bg-white border border-stone-mid rounded-xl px-4 py-3 mb-4 flex gap-3 items-center flex-wrap">
        <div className="relative flex-1 min-w-[280px]">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-soft pointer-events-none" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('anagrafiche.produttori.cerca_placeholder')}
            className="w-full pl-9 pr-3 py-2 text-sm border border-stone-border rounded-md focus:outline-none focus:ring-2 focus:ring-sage focus:border-sage text-ink-dark"
          />
        </div>
        {search && (
          <button
            type="button"
            onClick={() => setSearch('')}
            className="text-sm text-ink-soft hover:text-ink-dark transition-colors"
          >
            {t('anagrafiche.produttori.pulisci')}
          </button>
        )}
        <button
          type="button"
          onClick={() => setSoloInScadenza(!soloInScadenza)}
          data-testid="filtro-omologhe-scadenza"
          className={`flex items-center gap-1.5 px-3 py-2 text-sm border rounded-md transition-colors ${
            soloInScadenza
              ? 'border-amber-300 bg-amber-50 text-amber-700 font-medium'
              : 'border-stone-border text-ink-mid hover:bg-stone-bg'
          }`}
        >
          <Clock size={14} />
          {t('anagrafiche.produttori.omologhe_scadenza')}
          {soloInScadenza && <X size={13} />}
        </button>
      </div>

      {/* Contenuto */}
      {error ? (
        <div className="bg-accent-danger-bg border border-[#FECACA] rounded-xl p-6 text-center">
          <p className="text-accent-danger font-medium mb-3">{error}</p>
          <button type="button" onClick={fetchData} className="text-sm text-sage underline">
            {t('anagrafiche.produttori.riprova')}
          </button>
        </div>
      ) : !loading && items.length === 0 ? (
        <div className="bg-white border border-stone-mid rounded-xl p-12 text-center">
          <Building2 size={40} className="text-ink-faint mx-auto mb-3" />
          <p className="text-ink-mid font-medium">{t('anagrafiche.produttori.vuoto')}</p>
          {(debouncedSearch || soloInScadenza) && (
            <p className="text-ink-soft text-sm mt-1">{t('anagrafiche.produttori.modifica_filtri')}</p>
          )}
          {canCreate && !debouncedSearch && !soloInScadenza && (
            <button
              type="button"
              onClick={() => navigate('/produttori/nuovo')}
              className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-sage text-white rounded-lg text-sm font-medium hover:bg-sage-dark transition-colors"
            >
              <Plus size={14} />
              {t('anagrafiche.produttori.aggiungi_primo')}
            </button>
          )}
        </div>
      ) : (
        <div className="bg-white border border-stone-mid rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-stone-mid bg-stone-bg">
                {[t('anagrafiche.campi.ragione_sociale'), t('anagrafiche.produttori.col_piva'), t('anagrafiche.campi.codice_fiscale'), t('anagrafiche.campi.tipo_soggetto'), t('anagrafiche.produttori.col_omologhe'), t('anagrafiche.produttori.col_aggiornato')].map((h) => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-ink-soft uppercase tracking-wide">
                    {h}
                  </th>
                ))}
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {loading
                ? Array.from({ length: 8 }).map((_, i) => <SkeletonRow key={i} cols={7} />)
                : items.map((p) => {
                    const tipo = tipoSoggettoBadge(p.tipo_soggetto);
                    const omo = omologheBadge(p.stato_omologhe);
                    return (
                      <tr
                        key={p.id}
                        className="border-b border-stone-mid last:border-0 hover:bg-stone-bg transition-colors cursor-pointer"
                        onClick={() => navigate(`/produttori/${p.id}`)}
                      >
                        <td className="px-4 py-3 font-medium text-ink-dark max-w-[220px] truncate">
                          {p.ragione_sociale}
                        </td>
                        <td className="px-4 py-3 font-mono text-xs text-ink-mid">
                          {p.piva ?? <span className="text-ink-faint">—</span>}
                        </td>
                        <td className="px-4 py-3 font-mono text-xs text-ink-mid">
                          {p.codice_fiscale ?? <span className="text-ink-faint">—</span>}
                        </td>
                        <td className="px-4 py-3">
                          {tipo ? (
                            <Badge variant={tipo.variant}>{tipo.label}</Badge>
                          ) : (
                            <span className="text-ink-faint">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <Badge variant={omo.variant}>{omo.label}</Badge>
                        </td>
                        <td className="px-4 py-3 text-ink-soft text-xs">
                          {formatDateIT(p.updated_at)}
                        </td>
                        <td className="px-4 py-3">
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); navigate(`/produttori/${p.id}`); }}
                            className="px-3 py-1.5 text-xs font-medium text-sage border border-sage rounded-md hover:bg-sage-light transition-colors"
                          >
                            {t('anagrafiche.produttori.apri')}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
            </tbody>
          </table>

          {!loading && data && (
            <div className="px-4 py-3 border-t border-stone-mid bg-stone-bg flex items-center justify-between flex-wrap gap-3">
              <span className="text-xs text-ink-soft">
                {t('anagrafiche.produttori.footer', { items: items.length, total: data.total, page: data.page, pages: data.pages })}
              </span>
              <Pagination page={data.page} pages={data.pages} onPage={setPage} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
